import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { 
  VStack, 
  FormControl, 
  FormErrorMessage, 
  Button, 
  HStack, 
  Checkbox,
  CheckboxGroup,
  Box,
  Text
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import {
  CommunityInformationFormData,
  communityInformationSchema,
} from "../../schemas/schema";
import { CommunityTalent } from "__generated__/graphql";

interface CommunityInformationStepProps {
  onStepComplete: (data: CommunityInformationFormData) => void;
  onPrevious: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<CommunityInformationFormData>;
}

const COMMUNITY_TALENT_OPTIONS = [
  {
    value: CommunityTalent.Alumni,
    label: "Alumni",
    description:
      "Graduates who have completed their studies more than 1 year ago",
  },
  {
    value: CommunityTalent.RecentGraduates,
    label: "Recent Graduates",
    description: "Fresh graduates within the last 1 year",
  },
  {
    value: CommunityTalent.CurrentStudents,
    label: "Current Students",
    description: "Students currently enrolled in programs",
  },
];

export const CommunityInformationStep: React.FC<CommunityInformationStepProps> = ({
  onStepComplete,
  onPrevious,
  isLoading = false,
  defaultValues,
}) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CommunityInformationFormData>({
    resolver: zodResolver(communityInformationSchema),
    defaultValues: {
      ...defaultValues,
      communityTalent: defaultValues?.communityTalent || [],
    },
  });

  const onSubmit = (data: CommunityInformationFormData) => onStepComplete(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.typeOfCommunity}>
          <ThemedFormLabel>
            What type of community are you managing?
          </ThemedFormLabel>
          <ThemedInput
            {...register("typeOfCommunity")}
            placeholder="e.g., Blue collar workers, Tech Enthusiasts"
          />
          <FormErrorMessage>{errors.typeOfCommunity?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.communitySize}>
          <ThemedFormLabel>How big is your community?</ThemedFormLabel>
          <ThemedInput
            {...register("communitySize")}
            placeholder="e.g., 500+, 100-500"
          />
          <FormErrorMessage>{errors.communitySize?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.communityTalent}>
          <ThemedFormLabel>
            What type of talents are you looking to manage? (Select all that apply)
          </ThemedFormLabel>
          <Controller
            name="communityTalent"
            control={control}
            render={({ field }) => (
              <CheckboxGroup value={field.value} onChange={field.onChange}>
                <VStack align="stretch" spacing={3}>
                  {COMMUNITY_TALENT_OPTIONS.map((option) => (
                    <Box
                      key={option.value}
                      p={3}
                      border="1px"
                      borderColor="gray.200"
                      rounded="none"
                      cursor="pointer"
                      _hover={{
                        borderColor: "flatteredFlamingo.500",
                        bg: "gray.50",
                      }}
                    >
                      <Checkbox value={option.value}>
                        <VStack align="start" spacing={0.5} pl={2}>
                          <Text fontSize="md" fontWeight="medium">
                            {option.label}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {option.description}
                          </Text>
                        </VStack>
                      </Checkbox>
                    </Box>
                  ))}
                </VStack>
              </CheckboxGroup>
            )}
          />
          <FormErrorMessage>{errors.communityTalent?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.majorChallenges}>
          <ThemedFormLabel>What are your major challenges?</ThemedFormLabel>
          <ThemedInput
            {...register("majorChallenges")}
            placeholder="Briefly describe the main challenges"
          />
          <FormErrorMessage>{errors.majorChallenges?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <ThemedFormLabel>
            What digital tool(s) do you use to manage your community? (optional)
          </ThemedFormLabel>
          <ThemedInput
            {...register("digitalToolsUsed")}
            placeholder="e.g., Salesforce, Slack, Telegram"
          />
        </FormControl>

        <HStack spacing={4} mt={4}>
          <Button
            type="button"
            onClick={onPrevious}
            leftIcon={<ArrowBackIcon />}
            size="lg"
            flex="1"
            variant="outline"
            isDisabled={isLoading}
          >
            Previous
          </Button>
          <Button
            type="submit"
            size="lg"
            flex="1"
            colorScheme="flatteredFlamingo"
            rightIcon={<ArrowForwardIcon />}
            isLoading={isLoading}
            loadingText="Processing..."
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};


